// Utils
import classnames from 'classnames'
import { useTranslation } from 'next-i18next'

import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import { RichText, RichTextProps } from '../../molecules'
import Column from '../column/Column'
import Swiper from '../swiper'
import { SwiperProps } from '../swiper/Swiper'

import styles from './transparentPricing.module.scss'

export interface TransparentItemProps {
  name: string
  value: number
}

interface TransparentPricingProps {
  headerRichTextProps?: RichTextProps
  bodyRichTextProps?: RichTextProps
  items: TransparentItemProps[]
  quincePrice: number
  traditionalPrice: number
  className?: string
  currency?: string
  header?: string
  body?: string
}

const COMMON_SLIDER_SETTINGS: SwiperProps = {
  children: [],
  // @i18n-ignore
  id: 'transparent-pricing-slider',
  numberOfVisibleSlides: 2,
  showNavigator: false,
}

const MOBILE_SLIDER_SETTINGS = {
  ...COMMON_SLIDER_SETTINGS,
  numberOfVisibleSlides: 1,
  dots: true,
}

const DESKTOP_SLIDER_SETTINGS = {
  ...COMMON_SLIDER_SETTINGS,
}

const TransparentPricing = ({ items = [], currency = '$', ...props }: TransparentPricingProps) => {
  const deviceInfo = useDeviceInfo()
  const { t } = useTranslation('common')
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const classes = classnames(styles.container, props.className)
  const totalPrice = (items || []).reduce((total, curr) => total + curr.value, 0)
  const settings = isMobile ? MOBILE_SLIDER_SETTINGS : DESKTOP_SLIDER_SETTINGS

  return (
    <div className={classes}>
      <div className={styles.container__text}>
        <div className={styles.container__text__header}>
          {props.headerRichTextProps ? (
            <RichText {...props.headerRichTextProps} />
          ) : (
            props.header || t('transparent_pricing')
          )}
        </div>
        <div className={styles.container__text__body}>
          {props.bodyRichTextProps ? (
            <RichText {...props.bodyRichTextProps} />
          ) : (
            props.body || t('transparent_pricing_body')
          )}
        </div>
      </div>
      <div className={styles.container__right}>
        <Swiper {...settings} className={styles.container__slider}>
          <table className={styles.container__right__data}>
            <tbody>
              {items.map((item, key) => {
                return (
                  <tr className={styles.container__right__data__row} key={key}>
                    <th className={styles.container__right__data__row__text}>{item.name}</th>
                    <td className={styles.container__right__data__row__price}>
                      {currency}
                      {item.value.toFixed(2)}
                    </td>
                  </tr>
                )
              })}
              {items.length > 0 && (
                <tr className={classnames(styles.container__right__data__row, styles.container__right__data__total)}>
                  <th className={styles.container__right__data__total__text}>{t('total_cost')}</th>
                  <td className={styles.container__right__data__total__text}>
                    {currency}
                    {totalPrice.toFixed(2)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className={styles.container__right__columns}>
            {props.traditionalPrice ? (
              <div className={styles.container__right__columns__body}>
                <Column
                  // @i18n-ignore
                  header={`${currency}${(Math.round(props.quincePrice * 100) / 100).toFixed(2)}`}
                  className={styles.container__right__columns__column}
                  smallerUpper={props.traditionalPrice > props.quincePrice}
                  internalText={t('total_cost')}
                />
                <Column
                  // @i18n-ignore
                  header={`${currency}${(Math.round(props.traditionalPrice * 100) / 100).toFixed(2)}`}
                  footer={t('traditional_retailer')}
                  showInfoTooltip
                  internalText={t('total_cost')}
                  infoTooltipText={t('traditional_retailer_costs_are_an_approximation_based_on_our_kno')}
                />
              </div>
            ) : null}
          </div>
        </Swiper>
      </div>
    </div>
  )
}

export default TransparentPricing
